import { useEffect, useState } from "react";
import QuizRating from "../components/QuizRating";
import Pagination, { QUIZ_GRID_PAGE_SIZE } from "../components/Pagination";
import { apiUrl } from "../utils/api";
import "../style/Quiz.css";

const AUTHOR_PROFILE_API_URL = process.env.REACT_APP_AUTHOR_PROFILE_API_URL
    || apiUrl("author_profile.php");

export default function AuthorProfilePage({
    authorId,
    quizzes,
    user,
    text,
    onBack,
    onStartQuiz,
    onRatingUpdated
}) {
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [quizPage, setQuizPage] = useState(1);

    useEffect(() => {
        setQuizPage(1);
        const controller = new AbortController();
        fetch(`${AUTHOR_PROFILE_API_URL}?id=${encodeURIComponent(authorId)}`, {
            signal: controller.signal
        })
            .then(async (response) => {
                const result = await response.json();
                if (!response.ok) {
                    throw new Error(result.error || `Author API returned HTTP ${response.status}`);
                }
                if (!result.author || !Array.isArray(result.quizzes)) {
                    throw new Error("Author API returned invalid profile data");
                }
                setProfile(result);
            })
            .catch((requestError) => {
                if (requestError.name !== "AbortError") {
                    console.error("Failed to load author profile:", requestError);
                    setError(text.authorProfileError);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });
        return () => controller.abort();
    }, [authorId, text.authorProfileError]);

    const quizPageCount = Math.ceil(
        (profile?.quizzes.length || 0) / QUIZ_GRID_PAGE_SIZE
    );
    const currentQuizPage = Math.min(quizPage, Math.max(1, quizPageCount));
    const paginatedQuizzes = profile?.quizzes.slice(
        (currentQuizPage - 1) * QUIZ_GRID_PAGE_SIZE,
        currentQuizPage * QUIZ_GRID_PAGE_SIZE
    ) || [];

    return (
        <main className="author-profile-page">
            <button className="quiz-secondary-btn" type="button" onClick={onBack}>
                {text.backToQuizzes}
            </button>
            {isLoading ? (
                <p className="quiz-empty-state" role="status">{text.authorProfileLoading}</p>
            ) : error ? (
                <p className="quiz-empty-state" role="alert">{error}</p>
            ) : profile && (
                <>
                    <header className="author-profile-header">
                        {profile.author.profileImage ? (
                            <img
                                className="author-profile-avatar"
                                src={profile.author.profileImage}
                                alt={profile.author.name}
                            />
                        ) : (
                            <div className="author-profile-avatar author-profile-placeholder"
                                aria-hidden="true">
                                {profile.author.name.slice(0, 1).toUpperCase()}
                            </div>
                        )}
                        <div>
                            <span className="quiz-label">{text.quizAuthorLabel}</span>
                            <h1>{profile.author.name}</h1>
                            <p>{profile.quizzes.length} {text.authoredQuizCount}</p>
                            {(profile.author.facebookUrl || profile.author.instagramUrl) && (
                                <div className="author-social-links">
                                    {profile.author.facebookUrl && (
                                        <a
                                            href={profile.author.facebookUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {text.facebookLabel}
                                        </a>
                                    )}
                                    {profile.author.instagramUrl && (
                                        <a
                                            href={profile.author.instagramUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {text.instagramLabel}
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    </header>
                    <section className="author-profile-quizzes">
                        <h2>{text.authorQuizzes}</h2>
                        {profile.quizzes.length === 0 ? (
                            <p className="quiz-empty-state">{text.noAuthorQuizzes}</p>
                        ) : (
                            <div className="quiz-catalog-grid">
                                {paginatedQuizzes.map((quiz) => {
                                    const catalogQuiz = quizzes.find(
                                        (item) => item.id === quiz.id
                                    );
                                    const rating = catalogQuiz || quiz;
                                    return (
                                        <article className="quiz-tile" key={quiz.id}>
                                            <div className="quiz-tile-meta">
                                                <span className={`quiz-difficulty ${quiz.difficulty}`}>
                                                    {quiz.difficulty === "hard"
                                                        ? text.hardDifficulty
                                                        : text.easyDifficulty}
                                                </span>
                                                {quiz.isNew && (
                                                    <span className="quiz-new-badge">{text.newQuiz}</span>
                                                )}
                                            </div>
                                            <div className="quiz-tile-classification">
                                                <span><strong>{text.schoolClassroom}:</strong> {quiz.schoolTrack}</span>
                                                <span><strong>{text.subjectLabel}:</strong> {quiz.category}</span>
                                            </div>
                                            <h2>{quiz.title}</h2>
                                            <p>{quiz.description}</p>
                                            <QuizRating
                                                quizId={quiz.id}
                                                averageRating={rating.averageRating}
                                                ratingCount={rating.ratingCount}
                                                userRating={rating.userRating}
                                                disabled={!catalogQuiz}
                                                text={text}
                                                onRated={(result) => onRatingUpdated(quiz.id, result)}
                                            />
                                            <span className="quiz-tile-count">
                                                {quiz.questionCount} {text.questions}
                                                {" · "}
                                                {quiz.participantCount} {text.quizParticipants}
                                            </span>
                                            <button
                                                className="quiz-btn"
                                                type="button"
                                                disabled={!catalogQuiz}
                                                onClick={() => onStartQuiz(catalogQuiz)}
                                            >
                                                {text.startQuiz}
                                            </button>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                        <Pagination
                            page={currentQuizPage}
                            pageSize={QUIZ_GRID_PAGE_SIZE}
                            totalItems={profile.quizzes.length}
                            text={text}
                            onPageChange={setQuizPage}
                        />
                    </section>
                </>
            )}
        </main>
    );
}
